import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Dropdown, MenuProps, Tooltip } from 'ALIASED-antd';
import { CheckSquareTwoTone, DownOutlined, StopTwoTone } from 'antd/icons';
import { useSessionAppointment } from '@hooks/useSessionAppointment';
import { useSessionFormState } from '@hooks/useSessionFormState';
import { useDnaAppointment } from '../hooks/useDnaAppointment';
import { useCancelAppointment } from '../hooks/useCancelAppointment';
import { useCompleteAppointment } from '../hooks/useCompleteAppointment';
import { useReleaseAppointment } from '../hooks/useReleaseAppointment';
import { useHeidiEnabled } from '@hooks/useHeidiEnabled';
import { useHeidiSession } from '@components/Clinicians/Session/hooks/useHeidiSession';

type MenuItem = Required<MenuProps>['items'][number];
type MenuItems = MenuItem[];

const EndAppointmentDropdown = () => {
  const [isLetterAccuracyConfirmed] = useSessionFormState('isLetterAccuracyConfirmed');
  const { t } = useTranslation('common');
  const { appointment } = useSessionAppointment();

  const { onDnaAppointment } = useDnaAppointment();
  const { onCancelAppointment } = useCancelAppointment();
  const { onCompleteAppointment } = useCompleteAppointment();
  const { onReleaseAppointment } = useReleaseAppointment();

  const heidiEnabled = useHeidiEnabled(appointment.appointment_id);
  const { status: heidiStatus, hasHeidiError } = useHeidiSession();
  const isHeidiActive = heidiEnabled && heidiStatus !== 'idle' && !hasHeidiError;
  const isAuditAppointment = appointment?.appt_type_code === 'AUDIT';
  const isBlocked = isHeidiActive && !isAuditAppointment && !isLetterAccuracyConfirmed;

  const [, setAlert] = useSessionFormState('alert', null);

  const [endMenuOpen, setEndMenuOpen] = useState(false);
  const onEndMenuOpenChange = (open: boolean) => {
    setEndMenuOpen(open);
  };

  const endMenuItems: { caseReview: MenuItems; appointment: MenuItems } = {
    caseReview: [
      {
        key: 'complete',
        icon: <CheckSquareTwoTone />,
        label: isBlocked ? (
          <Tooltip
            title="Please review the clinical letter in full and tick the confirmation box to complete"
            trigger={['hover', 'focus']}
          >
            <span style={{ color: 'rgba(0, 0, 0, 0.25)' }}>{t('Complete')}</span>
          </Tooltip>
        ) : (
          'Complete'
        ),
        disabled: isBlocked,
        onClick: () => {
          if (isBlocked) return;
          setAlert(null);
          onEndMenuOpenChange(false);
          onCompleteAppointment();
        }
      },
      {
        key: 'release',
        icon: <StopTwoTone twoToneColor="#eb2f96" />,
        label: 'Release',
        onClick: () => {
          setAlert(null);
          onEndMenuOpenChange(false);
          onReleaseAppointment();
        }
      }
    ],
    appointment: [
      {
        key: 'complete',
        icon: <CheckSquareTwoTone />,
        label: isBlocked ? (
          <Tooltip title="Please review the clinical letter in full and tick the confirmation box to complete">
            <span style={{ color: 'rgba(0, 0, 0, 0.25)' }}>{t('Complete')}</span>
          </Tooltip>
        ) : (
          'Complete'
        ),
        disabled: isBlocked,
        onClick: () => {
          if (isBlocked) return;
          setAlert(null);
          onEndMenuOpenChange(false);
          onCompleteAppointment();
        }
      },
      {
        key: 'cancel',
        icon: <StopTwoTone twoToneColor="#eb2f96" />,
        label: 'Cancel',
        onClick: () => {
          setAlert(null);
          onEndMenuOpenChange(false);
          onCancelAppointment();
        }
      },
      {
        key: 'dna',
        icon: <StopTwoTone twoToneColor="#eb2f96" />,
        label: 'DNA',
        onClick: () => {
          setAlert(null);
          onEndMenuOpenChange(false);
          onDnaAppointment();
        }
      }
    ]
  };

  return (
    <Dropdown
      className="endItem"
      menu={{
        items:
          endMenuItems[appointment.case_review && !appointment.appointment_completed ? 'caseReview' : 'appointment']
      }}
      onOpenChange={onEndMenuOpenChange}
      trigger={['click']}
      open={endMenuOpen}
      placement="bottom"
    >
      <Button type="link" data-testid="end-appointment">
        {t('End ')}
        <DownOutlined rotate={endMenuOpen ? 0 : 270} />
      </Button>
    </Dropdown>
  );
};

export default EndAppointmentDropdown;
